import { useEffect, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { TiktokAudio } from '@round/api';
import { toast } from '@round/creatorbase-ui';
import { useFindOrCreateTiktokAudio } from 'Modules/Plans/hooks/queries/tiktokAudio/useFindOrCreateTiktokAudio';

export type SoundChipState = {
    /** Minted on add, so a chip survives its identifier resolving to something else entirely. */
    key: string;
    identifier: string;
    /** Null while the identifier is still in flight — a chip has no third state. */
    audio: TiktokAudio | null;
};

/** What the endpoint takes. The input refuses the rest rather than letting the create be rejected. */
const MAX_AUDIOS = 20;

export const LIMIT_MESSAGE = `Only ${MAX_AUDIOS} sounds can be targeted.`;

const RESOLVE_FAILED_MESSAGE = 'Could not add';

/**
 * A sound is only a target once it has resolved to a `Music` id, so the chips are held here and the
 * `audios` field is a projection of the resolved ones. Nothing is cached per identifier: the same
 * paste resolves again, which is the price of running this through the find-or-create mutation
 * rather than a query per chip.
 */
export const useSoundChips = () => {
    const { setFieldValue } = useFormikContext();
    const { findOrCreate } = useFindOrCreateTiktokAudio();
    const [chips, setChips] = useState<SoundChipState[]>([]);
    const [limitMessage, setLimitMessage] = useState<string | null>(null);
    const lastKey = useRef(0);

    useEffect(() => {
        setFieldValue('audios', getResolvedAudioIds(chips));
    }, [chips, setFieldValue]);

    const resolve = async ({ key, identifier }: SoundChipState) => {
        try {
            const audio = await findOrCreate(identifier);
            setChips((current) => settleChip(current, key, audio));
        } catch (error) {
            // A chip that cannot resolve leaves nothing behind to correct, so the toast carries the
            // reason rather than the chip.
            setChips((current) => current.filter((chip) => chip.key !== key));
            toast.error(`${RESOLVE_FAILED_MESSAGE} ${identifier}`, { description: getResolveErrorMessage(error) });
        }
    };

    const add = (identifiers: string[]) => {
        const accepted = identifiers.slice(0, Math.max(MAX_AUDIOS - chips.length, 0));

        setLimitMessage(accepted.length < identifiers.length ? LIMIT_MESSAGE : null);

        if (!accepted.length) {
            return;
        }

        const added = accepted.map<SoundChipState>((identifier) => {
            lastKey.current += 1;
            return { key: String(lastKey.current), identifier, audio: null };
        });

        setChips((current) => [...current, ...added]);
        // A paste fans out: each chip settles on its own, and one failing leaves the rest alone.
        added.forEach((chip) => void resolve(chip));
    };

    const remove = (key: string) => {
        setLimitMessage(null);
        setChips((current) => current.filter((chip) => chip.key !== key));
    };

    return {
        chips,
        isResolving: chips.some((chip) => !chip.audio),
        limitMessage,
        add,
        remove,
    };
};

const getResolvedAudioIds = (chips: SoundChipState[]) =>
    chips.reduce<number[]>((ids, chip) => (chip.audio ? ids.concat(chip.audio.id) : ids), []);

/**
 * A chip removed while its request was in the air is gone by key, and its late result is discarded.
 * Two identifiers standing for one sound collapse here too — on the resolved id, not the typed
 * string, since a link and a bare id look nothing alike.
 */
const settleChip = (chips: SoundChipState[], key: string, audio: TiktokAudio) => {
    const isDropped = !chips.some((chip) => chip.key === key);
    if (isDropped) {
        return chips;
    }

    const isDuplicate = chips.some((chip) => chip.key !== key && chip.audio?.id === audio.id);
    if (isDuplicate) {
        return chips.filter((chip) => chip.key !== key);
    }

    return chips.map((chip) => (chip.key === key ? { ...chip, audio } : chip));
};

const getResolveErrorMessage = (error: unknown) => {
    if (error instanceof Error && error.message) {
        return error.message;
    }

    return undefined;
};
